import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * useContent(pageKey)
 * Loads published overrides for a page. If ?preview=1 is present and an admin
 * token exists in localStorage, loads draft overrides instead (private preview).
 * Returns a getter `t(key, fallback)`.
 */
export default function useContent(pageKey) {
  const [data, setData] = useState({});
  const [params] = useSearchParams();
  const isPreview = params.get("preview") === "1";
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("qr_admin_token");

  useEffect(() => {
    let cancelled = false;
    const path = isPreview && hasToken
      ? `/admin/content/${pageKey}/preview`
      : `/content/${pageKey}`;
    api
      .get(path)
      .then((r) => { if (!cancelled) setData(r.data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pageKey, isPreview, hasToken]);

  const t = (key, fallback) => {
    const v = data[key];
    return typeof v === "string" && v.trim() !== "" ? v : fallback;
  };
  // `has(key)` returns true when the key is explicitly present in CMS data,
  // even as an empty string. Lets components distinguish "unset" (fresh install)
  // from "explicitly cleared" (admin blanked the field to hide something).
  t.has = (key) => Object.prototype.hasOwnProperty.call(data, key);
  t.raw = (key) => data[key];
  // `body(key, defaultString)` is for multi-paragraph body copy where
  // "explicitly cleared" must render as empty (BodyText returns null).
  //   key absent      → defaultString (fresh install / never edited)
  //   key present ''  → ''            (admin cleared it to hide the section)
  //   key present str → str           (admin wrote new copy)
  t.body = (key, defaultString) => (t.has(key) ? (t.raw(key) || "") : defaultString);
  return t;
}
